import { useState } from 'react';
import { V2Alert, V2Badge, V2Button, V2Card } from '@sakhtar/design-system/react';
import { PURPOSE_LABELS, previewBody, routineStatus, timingSummary } from './smsModel.ts';
import { SmsRoutineEditor } from './SmsRoutineEditor.js';
import type { SmsRoutineKind, SmsRoutinesResponse, SmsTemplatesResponse } from './smsTypes.ts';

const STATUS_BADGE = {
  unconfigured: { tone: 'neutral', label: 'تنظیم نشده' },
  off: { tone: 'neutral', label: 'غیرفعال' },
  armed: { tone: 'success', label: 'فعال‌شده، در انتظار اتصال ارسال' },
} as const;

export function SmsRoutinesTab(props: {
  readonly base: string;
  readonly routines: SmsRoutinesResponse;
  readonly templates: SmsTemplatesResponse | null;
  readonly canManage: boolean;
  readonly onChanged: () => void;
  readonly onCreateTemplate: () => void;
}) {
  const [editing, setEditing] = useState<SmsRoutineKind | null>(null);
  const specs = props.routines.catalog.routines;
  const templateOf = (id: string | null) => props.templates?.items.find((t) => t.id === id) ?? null;

  return (
    <section className="sms-tab" aria-label="روال‌های پیامک">
      {!props.routines.delivery.connected ? (
        <V2Alert tone="warn" title="ارسال خودکار هنوز متصل نیست">
          {props.routines.delivery.message}
        </V2Alert>
      ) : null}

      <ul className="sms-routine-grid">
        {props.routines.items.map((routine) => {
          const spec = specs.find((s) => s.kind === routine.kind);
          if (!spec) return null;
          const status = routineStatus(routine);
          const badge = STATUS_BADGE[status];
          const template = templateOf(routine.templateId);
          return (
            <li key={routine.kind}>
              <V2Card>
                <article className="sms-routine" data-status={status}>
                  <header>
                    <div>
                      <h3>{spec.title}</h3>
                      <p>{spec.description}</p>
                    </div>
                    <V2Badge tone={badge.tone}>{badge.label}</V2Badge>
                  </header>

                  {status === 'unconfigured' ? (
                    <p className="sms-routine__hint">
                      هنوز قالب و زمان‌بندی این روال را تنظیم نکرده‌اید.
                    </p>
                  ) : (
                    <>
                      <p className="sms-routine__timing">{timingSummary(routine.settings)}</p>
                      {template ? (
                        <blockquote className="sms-routine__text">
                          <strong>{template.title}</strong>
                          {previewBody(template.body, props.templates?.variables ?? [])}
                        </blockquote>
                      ) : null}
                    </>
                  )}

                  <footer>
                    <V2Badge tone={spec.purpose === 'marketing' ? 'warn' : 'info'}>
                      {PURPOSE_LABELS[spec.purpose]}
                    </V2Badge>
                    {spec.requiresMarketingConsent ? <small>نیازمند رضایت بازاریابی</small> : null}
                    {props.canManage ? (
                      <V2Button
                        size="sm"
                        variant={status === 'unconfigured' ? 'primary' : 'secondary'}
                        disabled={!props.templates}
                        onClick={() => setEditing(routine.kind)}
                      >
                        {status === 'unconfigured' ? 'تنظیم روال' : 'ویرایش'}
                      </V2Button>
                    ) : null}
                  </footer>
                </article>
              </V2Card>
            </li>
          );
        })}
      </ul>

      {editing && props.templates
        ? (() => {
            const spec = specs.find((s) => s.kind === editing)!;
            const routine = props.routines.items.find((r) => r.kind === editing)!;
            return (
              <SmsRoutineEditor
                key={`${editing}-${routine.updatedAt ?? 'new'}`}
                base={props.base}
                spec={spec}
                routine={routine}
                templates={props.templates.items}
                variables={props.templates.variables}
                onClose={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  props.onChanged();
                }}
                onCreateTemplate={() => {
                  setEditing(null);
                  props.onCreateTemplate();
                }}
              />
            );
          })()
        : null}
    </section>
  );
}
